import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { User } from '@sharedcommon/model/user.model';
import { UserService } from '@sharedcommon/services/user.service';
import { ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { HeaderService } from '@sharedcommon/services/header.service';
import { JobStatusInfo, RawMaterialFilterDto, RawMaterialService } from '@sharedcommon/rest/data.restservice';
import { map } from 'rxjs/operators';
import { NeqSelectAutocompleteComponent } from '@sharedcommon/components/select-autocomplete/neq-select-autocomplete.component';
import { ValidationService } from '@sharedcommon/services/validation.service';
import { EntityDto } from '@sharedrest/identity.restservice';
import { SafeUrl } from '@angular/platform-browser';
import { NeqAdvancedRawmaterialsSearchPopinComponent } from '@usercommon/components/advanced-rawmaterials-search/advanced-rawmaterials-search-popin.component';
import { Rights } from '@sharedcommon/util/rights.util';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { NeqCalculateBatchPopinComponent } from '@usercommon/components/rawmaterials/calculate-batch-popin/calculate-batch-popin.component';
import { ImportSemiAutoStore } from '@usercommon/stores/import-semiauto.store';
import { SampleFilterDto } from '@sharedcommon/model/sample/filter/sample-filter.model';
import { SampleRestService } from '@sharedcommon/rest/sample/sample.restservice';
import { Constants } from '@sharedcommon/util/constants.util';
import { NeqAdvancedSamplesSearchPopinComponent } from '@usercommon/components/advanced-samples-search/advanced-samples-search-popin.component';
import {RmOrSampleMultiModificationStore} from "@usercommon/stores/rm-or-sample-multi-modification.store";
import { ProductPatchDto } from '@sharedcommon/model/product-patch.model';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'neq-header',
  templateUrl: './header.component.html'
})
export class NeqHeaderComponent implements OnInit, OnDestroy {

  @ViewChild(NeqSelectAutocompleteComponent) simpleSearch: NeqSelectAutocompleteComponent;
  @ViewChild('userDropdown') elementRefTest: ElementRef;
  public calculateWidth = true;
  public openDropdown = false;

  public show: boolean;
  public user: User;
  strongLabel: string;
  basicLabel: string;
  showLogo: boolean;
  showGoBack: boolean;
  userEntity: EntityDto;
  userEntityLogo: SafeUrl;
  userActivity: string;
  private readonly jobId: Map<string, string> = new Map<string, string>();
  private jobStatus: JobStatusInfo; // Only for CalculateBatch job

  private redirection: ActivatedRouteSnapshot | string;

  exportSettingsRight = [Rights.U_MP_EXPORT_SETTINGS];

  importSemiautoOnGoing:boolean = false;
  multimodificationOnGoing: boolean=false;

  constructor(
    private readonly userService: UserService,
    private readonly rawMatService: RawMaterialService,
    private readonly headerService: HeaderService,
    private readonly router: Router,
    private readonly dialogService: MatDialog,
    private readonly validationService: ValidationService,
    private readonly sampleService: SampleRestService,
    private readonly translateService: TranslateService,
    private readonly importSemiAutoStore: ImportSemiAutoStore,
    private readonly rmMultiModificationStore: RmOrSampleMultiModificationStore) {
    this.headerService.headerTitle$.subscribe(
      headerValues => {
        this.strongLabel = headerValues.strongLabel;
        this.basicLabel = headerValues.basicLabel;
        this.showLogo = headerValues.showLogo;
        this.showGoBack = headerValues.showGoBack;
        this.redirection = headerValues.redirectionRoute;
      }
    );
    userService.getJobId(Constants.JOB_TYPE_CALCULATE_BATCH).subscribe(value => {
      this.jobId.set(Constants.JOB_TYPE_CALCULATE_BATCH, value);
    });
    userService.getJobId(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO).subscribe(value => {
      this.jobId.set(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO, value);
    });
    userService.getJobStatus(Constants.JOB_TYPE_CALCULATE_BATCH).subscribe(value => {
      this.jobStatus= value;
    });
  }

  ngOnInit() {
    this.user = this.userService.getUser();
    this.userEntity = this.userService.getEntityDto();
    this.userEntityLogo = this.userService.getEntityLogo();
    this.userActivity = this.userService.getActivity();
    this.initSemiAuto();
    this.initMultiModification();
  }

  isAdmin(): boolean {
    return this.userService.isAdmin();
  }

  goRmAdvancedSearch() {

    const dialog = this.dialogService.open(NeqAdvancedRawmaterialsSearchPopinComponent);
    dialog.afterClosed().subscribe(data => {
      if (data) {
        if (data['rawMaterial']) {
          this.redirect(`/app/rawmaterials/input/${data['rawMaterial'].value.id}`);
        } else {
          this.redirect('/app/rawmaterials', {
            levelId: (data['level']) ? data['level'].id : null,
            predictionModelId: (data['predictionModel']) ? data['predictionModel'].value.id : null,
            referenceId: (data['reference']) ? data['reference'].value.id : null
          });
        }
      }
    });
  }

  goSampleAdvancedSearch() {
    this.dialogService.open(NeqAdvancedSamplesSearchPopinComponent);
  }

  logout() {
    const callback = () => {
      // unlock all raw materials locked by me before logout
      if (this.userService.getEntity()) {
        const unlockPatchAction: ProductPatchDto = { patchAction: 'UNLOCK_ENTITY' };
        this.rawMatService.lockedByMePatchLockedByMe(this.userService.getEntity(), unlockPatchAction).subscribe();
        this.sampleService.patchAllSampleLockedByCurrentUser(unlockPatchAction).subscribe();
      }
      this.importSemiAutoStore.reset();
      this.router.navigate(['logout']);
    };
    this.validationService.checkModifications(callback);
  }

  goLastPage() {
    if (this.headerService.valuesLoaded){
      const callback = () => {
        const url = this.redirection instanceof ActivatedRouteSnapshot ?
          this.redirection.pathFromRoot.reduce((acc, current) => acc + '/' + current.url.join('/'), '') : this.redirection;
        this.router.navigate([url]);
      };

      this.validationService.checkModifications(callback);
    }
  }

  fetchBaseRawmaterial = (search, page) => {
    const filters = new RawMaterialFilterDto();

    if (search) {
      filters.global = search;
    }
    filters.page = page;

    return lastValueFrom(
      this.rawMatService.fetch(this.userEntity.code, filters).pipe(
        map(x =>
          x.data
            .map(rm => {
                return { rawmaterial: rm, label: `${rm.code} - ${rm.label}` };
              }
            )
        )
      )
    );
  };

  fetchBaseSample = (searchTerm: string, page: number) => {
    const filters = new SampleFilterDto();
    if (searchTerm) {
      filters.code = searchTerm;
      filters.label = searchTerm;
      filters.farmLabel = searchTerm;
      filters.matchAny = true;
    }
    filters.page = page;
    filters.sort = [ { field: "validationDate", asc: false } ]

    return lastValueFrom(
      this.sampleService.searchSampleDashboardByEntityCode(this.userEntity.code, filters, false).pipe(
        map(x => x.data.map(sample => {
          return { sample: sample, label: `${sample.code} - ${sample.label}` };
        }))
      )
    );
  };

  onSelectRawmaterial(selection: any) {
    if (selection?.rawmaterial) {
      const callback = () => {
        this.router.navigate([`/app/rawmaterials/input/${selection.rawmaterial.id}`]);
      };
      this.validationService.checkModifications(callback);
    }
  }

  onSelectSample(selection: any) {
    if (selection?.sample) {
      const callback = () => {
        this.router.navigate([`/app/samples/input/${selection.sample.id}`]);
      };
      this.validationService.checkModifications(callback);
    }
  }

  redirect(url: string, queryParams?: Params | null) {
    const callback = () => {
      this.importSemiAutoStore.stopImport();
      this.router.navigate([url], { queryParams: queryParams });
    };
    this.validationService.checkModifications(callback);
  }

  displayChangeEntityMenu(): boolean {
    return this.userService.canChangeEntity();
  }

  showCalculateBatchJob(): boolean {
    return this.jobId.has(Constants.JOB_TYPE_CALCULATE_BATCH) && this.jobId.get(Constants.JOB_TYPE_CALCULATE_BATCH) !== '';
  }

  showImportFullAuto(): boolean {
    return this.jobId.has(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO) && this.jobId.get(Constants.JOB_TYPE_IMPORT_RM_FULL_AUTO) !== '';
  }

  showProgress(): boolean {
    switch (this.jobStatus.status) {
      case 'PENDING':
      case 'RUNNING':
        return true;
      default:
        return false;
    }
  }

  getProgressMode(): string {
    switch (this.jobStatus.status) {
      case 'PENDING':
        return 'query';
      case 'RUNNING':
        if (this.jobStatus.progress !== 0) {
          return 'determinate';
        } else {
          return 'query';
        }
      default:
        return '';
    }
  }

  getProgressValue(): number {
    return this.jobStatus.progress;
  }

  isFeedOperatingMode(): boolean {
    return this.userService.isFeedOperatingMode();
  }

  goJobDetail() {
    this.dialogService.open(NeqCalculateBatchPopinComponent);
  }

  getJobStep(): string {
    switch (this.jobStatus.status) {
      case 'PENDING':
        return this.translateService.instant('msg.user.calculate-batch.step.pending');
      case 'RUNNING':
        return this.translateService.instant('msg.user.calculate-batch.step.running');
      case 'FINISHED':
        return this.translateService.instant('msg.user.calculate-batch.step.finished');
      case 'CANCELLED':
        console.error('Cannot manage cancelled job');
        return this.translateService.instant('msg.user.calculate-batch.step.cancelled');
    }
  }

  showResearch(): boolean {
    return !this.importSemiautoOnGoing && !this.multimodificationOnGoing;
  }

  initSemiAuto(): void {
    this.importSemiAutoStore.getSemiAutoProcessState()
    .subscribe((state: boolean) => {
      this.importSemiautoOnGoing = state;
    });
  }

  private initMultiModification(): void {
    this.rmMultiModificationStore.processState.subscribe(value => this.multimodificationOnGoing = value)

  }

  ngOnDestroy(): void {
    this.rmMultiModificationStore.stop();
  }
}
